// @flow

import * as React from 'react';
import {useEffect, useRef} from 'react';
import classNames from 'classnames';
import Box from 'sail/Box';
import Group from 'sail/Group';
import Button from 'sail/Button';
import {BodyAlt} from 'sail/Text';
import ContentTabs, {ContentTab} from 'sail/ContentTabs';
import {MenuItem, OverflowMenu} from 'sail/Menu';
import {InlineLink} from 'sail/InlineLink';
import {useNavigation, useAccount, useLocation, useAnalytics} from 'docs/data';
import type {NavigationTopic} from 'docs/data';
import {FormattedMessage} from 'react-intl';
import {normalizeHostname} from 'docs/util';
import {Logo} from '../sidebar/Logo';
import {DashboardLink} from './DashboardLink';
import {AccountSwitcher} from './AccountSwitcher';
import {SidebarInfoTooltip} from '../sidebar/navigation/SidebarInfoTooltip';
import {RefDropdown} from './RefDropdown';
import {Search} from './search/Search';
import {SupportLink} from './SupportLink';

import {headerMessages} from './Header.messages';
import {MobileNavRightLink, MobileNav} from './MobileNav';
import './Header.scss';

const HeaderAccountView = () => {
  const {account} = useAccount();
  const merchant = account.merchant;

  return merchant?.isLoggedIn ? <AccountSwitcher /> : <LoggedOutView />;
};

const LoggedOutView = () => {
  const {account} = useAccount();
  const {location} = useLocation();
  return (
    <Group direction="horizontal" spacing={24} alignItems="center">
      <BodyAlt>
        <InlineLink
          href={normalizeHostname(
            'https://dashboard.stripe.com/register',
            account.hosts,
          )}
        >
          <FormattedMessage {...headerMessages.createAccount} />
        </InlineLink>
      </BodyAlt>
      <Button
        color="white"
        label={<FormattedMessage {...headerMessages.signIn} />}
        href={normalizeHostname(
          `https://dashboard.stripe.com/login?redirect=${location.toString()}`,
          account.hosts,
        )}
        target="_self"
      />
    </Group>
  );
};

type HeaderContentTabsProps = {|
  topics: Array<NavigationTopic>,
  contentTabsRef: React.ElementRef<any>,
|};

const HeaderContentTabs = ({
  topics,
  contentTabsRef,
}: HeaderContentTabsProps) => {
  const {trackAction} = useAnalytics();

  return (
    <ContentTabs ref={contentTabsRef} size="small">
      {topics.map((topic) => {
        return (
          <ContentTab
            key={topic.text}
            size="small"
            active={topic.active}
            href={topic.href}
            onClick={() =>
              trackAction('docs_top_nav_clicked', {
                link: topic.href,
                text: topic.text,
              })
            }
          >
            <BodyAlt
              color={topic.active ? 'blue' : 'default'}
              className={classNames('Header-tab-text', {
                'Header-tab-text-active': topic.active,
              })}
            >
              {topic.text}
            </BodyAlt>
          </ContentTab>
        );
      })}
    </ContentTabs>
  );
};

/*
 * Displays the first X topics (where X is the limit parameter) and hides the rest in an OverflowMenu
 */
type HeaderMenuProps = {|
  limit?: number,
  className?: string,
|};

const HeaderMenu = ({limit = 3, className = ''}: HeaderMenuProps) => {
  const {navigation} = useNavigation();
  const ref = useRef();

  useEffect(() => {
    // Manually set the active ContentTab based on active topic
    const index = navigation.topics.findIndex((t) => t.active);
    if (ref?.current) {
      ref.current.setActiveTab(index === -1 ? 0 : index);
    }
  }, [ref, navigation]);

  const handleClick = () => {
    // Manually clear the active ContentTab if someone selects
    // a topic from the OverflowMenu
    if (ref?.current) {
      ref.current.setActiveTab(null);
    }
  };

  const menuTopics = navigation.topics.slice(limit);
  const {pushLocation} = useLocation();

  return (
    <Group
      direction="horizontal"
      spacing={8}
      className={classNames('Header-menu', className)}
    >
      <HeaderContentTabs
        contentTabsRef={ref}
        topics={navigation.topics.slice(0, limit)}
      />
      {menuTopics.length > 0 && (
        <OverflowMenu hoverable>
          {menuTopics.map((topic) => (
            <MenuItem
              key={topic.href}
              className={classNames(
                'Header-menuItem',
                topic.active && 'active',
              )}
              onClick={() => {
                handleClick();
                pushLocation(topic.href);
              }}
            >
              {topic.text} <SidebarInfoTooltip xray={topic.xray} />
            </MenuItem>
          ))}
        </OverflowMenu>
      )}
    </Group>
  );
};

// This number is to ensure we only show the public topics in the top section
const NUMBER_OF_PUBLIC_TOPICS = 7;

type HeaderProps = {|
  isMobileSearchOpen: boolean,
  onToggleNavSearch: () => void,
|};

export const Header = ({
  isMobileSearchOpen,
  onToggleNavSearch,
}: HeaderProps) => {
  return (
    <Box
      className={classNames(
        'Header',
        isMobileSearchOpen && 'Header--no-border',
      )}
      flex={{direction: 'column', justifyContent: 'space-between'}}
    >
      <Box className="Header--small">
        <MobileNav
          isMobileSearchOpen={isMobileSearchOpen}
          onToggleNavSearch={onToggleNavSearch}
        />
        <MobileNavRightLink />
      </Box>
      <Box className="Header--full">
        <Box
          className="Header-top"
          background={'offset'}
          flex={{justifyContent: 'space-between', alignItems: 'center'}}
          margin={{left: 4}}
        >
          <Box
            background={'offset'}
            flex={{alignItems: 'center'}}
            flexBasis="50%"
            style={{height: '48px'}}
          >
            <Logo />
            <Search />
          </Box>
          <Box padding={{left: 32, right: 24}} flex={{alignItems: 'center'}}>
            <Group
              direction="horizontal"
              spacing={{horizontal: 24}}
              alignItems="center"
            >
              <HeaderAccountView />
            </Group>
          </Box>
        </Box>
        <Box
          className="Header-collapsable"
          flex={{alignItems: 'flex-end', justifyContent: 'space-between'}}
          padding={{left: 24}}
          background={'offset'}
          divider={{color: 'light', width: 1, side: 'bottom'}}
        >
          <HeaderMenu
            limit={NUMBER_OF_PUBLIC_TOPICS}
            className="Header-menu--widest"
          />
          <HeaderMenu limit={3} className="Header-menu--skinny" />
          <HeaderMenu limit={5} className="Header-menu--wide" />
          <Group
            direction="horizontal"
            spacing={24}
            margin={{right: 24}}
            style={{marginBottom: '10px'}}
          >
            <RefDropdown />
            <SupportLink />
            <DashboardLink />
          </Group>
        </Box>
      </Box>
    </Box>
  );
};
